<template>
  <div class="flex flex-col sm:flex-row sm:items-center items-end">
    <TestSend
      :sending-test="sendingTest"
      :loading="loading"
      class="test-send-link md:mr-3"
      @test-send="$emit('test-send')"
    />

    <Button class="link download-send-link sm:mr-3" @click="handleDownload"
      ><v-icon slot="icon-start" name="download" scale="0.9" class="mr-1" />
      Download</Button
    >

    <Button
      class="send-button ml-auto sm:ml-0"
      :disabled="loadingActive"
      :loading="loadingActive"
      title="Ready to roll 🚀"
      @click="approveSend"
    >
      <v-icon slot="icon-start" name="paper-plane" scale="0.9" class="mr-1" />

      {{ loadingActive ? "Loading..." : "Approve to Send" }}
    </Button>
  </div>
</template>

<script>
import TestSend from "@/components/Editor/TestSend";
import { SendCampaignCheck } from "@/graphql/queries";
import EventBus from "@/lib/event-bus.js";
import { isAuthError } from "@/lib/auth";

export default {
  apollo: {
    sendCampaignCheck: {
      query: () => SendCampaignCheck,
      variables: function() {
        return {
          input: { campaignId: this.$route.params.id },
        };
      },
      manual: true,
      async result({ data, loading }) {
        this.sendCampaignCheckCalled = true;

        if (!loading) {
          const responseOk = await this.checkResponseData(data);

          responseOk
            ? this.handleSendCampaignCheckResult(data)
            : this.handleError("sendCampaignCheck response not ok");
        }
      },
      error({ graphQLErrors }) {
        this.loadingActive = false;
        let error = "";

        if (graphQLErrors.length) {
          // If we have an auth error return false here as the login modal should open...
          if (isAuthError(graphQLErrors)) return false;

          // Map over the GraphQL errors and grab the first one.
          const errors = graphQLErrors.map(({ message }) => message);
          error = `GraphQL Error: ${errors[0]}`;
        }

        this.handleError(error);
      },
      skip: true,
    },
  },
  name: "SendControls",
  components: { TestSend },
  props: {
    loading: Boolean,
    sendingTest: Boolean,
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loadingActive: this.loading,
      queryStringAction: "",
      sendCampaignCheck: null,
    };
  },
  mounted() {
    this.setupEventBus();
    this.queryStringCheck();
  },
  beforeDestroy() {
    this.removeEventBus();
  },
  methods: {
    approveSend() {
      this.loadingActive = true;

      this.$emit("approve-send");

      // If the query's been called then refetch it, if not start it...
      this.sendCampaignCheckCalled
        ? this.$apollo.queries.sendCampaignCheck.refetch()
        : this.$apollo.queries.sendCampaignCheck.start();
    },
    checkResponseData(data) {
      return (
        this.$has(data, "sendCampaignCheck.packageCheck.upgradeRequired") &&
        this.$has(data, "sendCampaignCheck.campaignCheck") &&
        this.$has(data, "sendCampaignCheck.details")
      );
    },
    handleDownload() {
      this.$modal.show("download-campaign", {
        campaignId: this.$route.params.id,
      });
    },
    handleError(error) {
      this.loadingActive = false;

      window.Sentry && window.Sentry.captureException(error);

      this.$modal.show("dialog", {
        title: "Oh no 😯",
        text:
          "There was an error. Please try again. If the problem persists please contact our support team.",
        buttons: [
          {
            title: "Close",
          },
        ],
      });
    },
    handleSend() {
      // Show the send modal...
      this.$modal.show("send-campaign", {
        campaignId: this.$route.params.id,
        details: this.sendCampaignCheck.details,
        campaignCheck: this.sendCampaignCheck.campaignCheck,
        gtmDownloadEvent: "promo-download-campaign-paid",
      });

      this.loadingActive = false;
    },
    handleSendCampaignCheckResult(data) {
      this.sendCampaignCheck = data.sendCampaignCheck;
      const { upgradeRequired } = data.sendCampaignCheck.packageCheck;

      // Handle send if no upgrade is required...
      if (!upgradeRequired) return this.handleSend();

      // Send to upgrade screen only if there's no queryStringAction
      // Otherwise the user will have clicked on a link and end up on upgrade screen instead of a campaign e.g from a test send action
      if (!this.queryStringAction) {
        this.handleUpgrade();
      }

      this.queryStringAction = "";
      this.loadingActive = false;
    },
    handleUpgrade() {
      // Track the sendCampaignCheck in analytics...
      this.trackSendCampaignCheck();

      // Remove the query string from the route
      this.$router.replace(`/campaign/${this.$route.params.id}`);

      // Redirect the user to the upgrade route and pass through upgrade details...
      this.$router.push({
        name: "Upgrade",
        query: {
          c: this.$route.params.id,
          a: this.accountId,
        },
      });
    },
    queryStringCheck() {
      const { action, approveSend } = this.$route?.query;

      this.queryStringAction = action;

      if (approveSend || action === "send") this.approveSend();

      if (action === "download") this.handleDownload();

      this.removeQueryStrings();
    },
    removeQueryStrings() {
      // Remove the approveSend=true from the query string so the user doesn't see it again upon refresh
      this.$router.replace(`/campaign/${this.$route.params.id}`);
    },
    removeEventBus() {
      EventBus.$off("approve-send");
    },
    setupEventBus() {
      EventBus.$on("approve-send", () => {
        this.approveSend();
      });
    },
    trackSendCampaignCheck() {
      const { requiredPackage } = this.sendCampaignCheck.packageCheck;

      this.$gtm.trackEvent({
        event: "promo-upgrade-required",
        accountId: this.accountId,
        campaignId: this.$route.params.id,
        packageName: requiredPackage.name,
        packagePrice: requiredPackage.price,
      });
    },
  },
  sendCampaignCheckCalled: false,
};
</script>

<style scoped>
.send-button {
  @apply p-0 bg-transparent text-success no-underline;

  &[disabled],
  &:hover[disabled] {
    @apply bg-transparent text-support-lighter;
  }

  @screen md {
    @apply text-base py-1 px-4 bg-success text-white border-success border-2 shadow;

    text-shadow: 0px 1px 3px #326536;

    &[disabled],
    &:hover[disabled] {
      @apply bg-support-dark border-support-lighter text-support-lighter;
    }

    >>> svg {
      filter: drop-shadow(0px 1px 2px #326536);
    }

    &:hover {
      @apply bg-brand border-brand;
      text-shadow: 0px 1px 3px #353a61;

      >>> svg {
        filter: drop-shadow(0px 1px 2px #353a61);
      }
    }

    &:focus {
      @apply shadow-inner;
    }
  }
}

.send-link {
  @apply font-lato text-brand-lighter p-0;

  &:hover {
    @apply no-underline text-success;
  }
}

.test-send-link {
  @apply .send-link hidden;

  @screen md {
    @apply flex;
  }
}

.download-send-link {
  @apply .send-link;

  @screen md {
    @apply text-brand-alt;
  }
}
</style>
